/**
 * Utility functions for handling product slugs
 */

import type { DutchieProduct, ProductDisplay } from "@/lib/api/dutchie";

/**
 * Creates a SEO-friendly slug from a product name and strain type
 */
export function createProductSlug(product: ProductDisplay | DutchieProduct): string {
  // Get the product name
  const name = product.name.toLowerCase();
  
  // Add brand if available
  let slug = '';
  
  if ('brand' in product && product.brand) {
    // If it's a string (which means it's a ProductDisplay)
    if (typeof product.brand === 'string') {
      slug = `${product.brand.toLowerCase()}-`;
    } 
    // If it's an object (which means it's a DutchieProduct)
    else if (product.brand && typeof product.brand === 'object' && product.brand.name) {
      slug = `${product.brand.name.toLowerCase()}-`;
    }
  }
  
  // Add product name
  slug += name;
  
  // Add strain type if available
  if (product.strainType) {
    slug += `-${product.strainType.toLowerCase()}`;
  }
  
  // Add category type if available
  if ('category' in product && product.category) {
    slug += `-${product.category.toLowerCase()}`;
  }
  
  // Clean the slug by replacing non-alphanumeric characters with dashes
  slug = slug.replace(/[^a-z0-9]+/g, '-');
  
  // Remove any leading or trailing dashes
  slug = slug.replace(/^-|-$/g, '');
  
  return slug;
}

/**
 * Maps a slug back to a product ID by comparing with all products
 */
export async function findProductBySlug(slug: string, getProductsFn: () => Promise<ProductDisplay[]>): Promise<ProductDisplay | null> {
  // Get all products
  const products = await getProductsFn();
  
  // Create a lowercase version of the slug for comparison
  const normalizedSlug = slug.toLowerCase();
  
  // Try to find an exact slug match first
  for (const product of products) {
    if (createProductSlug(product) === normalizedSlug) {
      return product;
    }
  }
  
  // If no exact match, try to find a product where the slug contains the search slug
  // This provides some fallback for URL variations
  for (const product of products) {
    if (createProductSlug(product).includes(normalizedSlug) ||
        normalizedSlug.includes(createProductSlug(product))) {
      return product;
    }
  }
  
  // If still no match, try to match by product name only
  for (const product of products) {
    const nameSlug = product.name.toLowerCase().replace(/[^a-z0-9]+/g, '-');
    if (normalizedSlug.includes(nameSlug) || nameSlug.includes(normalizedSlug)) {
      return product;
    }
  }
  
  return null;
}

// Common noise words in slugs that don't identify a product.
const SLUG_STOP_WORDS = new Set([
  "flower","pre","rolls","preroll","joint","joints","vape","vapes","vaporizer","vaporizers",
  "concentrate","concentrates","edible","edibles","tincture","tinctures","topical","topicals",
  "oral","orals","apparel","accessories",
  "indica","sativa","hybrid",
  "g","mg","oz","pack","packs","ct",
  "the","and","or","of","with","for",
]);

/**
 * When a product URL 404s (renamed, delisted, old link from Google), find the
 * closest still-available product by scoring each product's slug against the
 * requested slug's tokens. Returns null if nothing scores above a small
 * threshold, so obviously-unrelated URLs still 404 cleanly.
 */
export async function findSimilarProductBySlug(
  slug: string,
  getProductsFn: () => Promise<ProductDisplay[]>
): Promise<ProductDisplay | null> {
  const wanted = slug
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter((w) => w.length > 1 && !/^\d+$/.test(w) && !SLUG_STOP_WORDS.has(w));
  if (wanted.length === 0) return null;

  const products = await getProductsFn();

  let best: { product: ProductDisplay; score: number } | null = null;
  for (const product of products) {
    const candTokens = new Set(
      createProductSlug(product)
        .split("-")
        .filter((w) => w.length > 1 && !SLUG_STOP_WORDS.has(w))
    );
    let score = 0;
    for (const w of wanted) if (candTokens.has(w)) score++;
    if (!best || score > best.score) best = { product, score };
  }

  // At least 2 non-stop-word tokens must match, and 25%+ of the requested
  // words, otherwise it isn't really the same product.
  const minMatches = Math.max(2, Math.ceil(wanted.length * 0.25));
  if (best && best.score >= minMatches) return best.product;
  return null;
}
